import { Component, inject } from "@angular/core";
import {
    RentVsBuyService,
    SLIDER_DEFS,
} from "../../services/rent-vs-buy.service";
import { ControlTab, RvbSliderDef } from "../../models/rent-vs-buy.models";
import { RvbFormatPipe } from "../../pipes/rvb-format.pipe";
import { NgOptimizedImage } from "@angular/common";

interface TabDef {
    id: ControlTab;
    label: string;
    icon: string;
}

@Component({
    selector: "hs-rvb-controls-panel",
    standalone: true,
    imports: [RvbFormatPipe, NgOptimizedImage],
    template: `
        <div class="controls">
            <div class="controls__header">
                <span class="controls__dot"></span>Adjust your scenario
            </div>

            <div class="controls__tabs" role="tablist">
                @for (tab of tabs; track tab.id) {
                    <button
                        class="controls__tab"
                        [class.controls__tab--active]="
                            svc.activeTab() === tab.id
                        "
                        role="tab"
                        [attr.aria-selected]="svc.activeTab() === tab.id"
                        (click)="svc.setActiveTab(tab.id)"
                    >
                        <img [ngSrc]="tab.icon" width="12" height="12" alt="" />
                        {{ tab.label }}
                    </button>
                }
            </div>

            <!-- BUY TAB -->
            @if (svc.activeTab() === "buy") {
                <div class="controls__panel" role="tabpanel">
                    @for (slider of buySliders; track slider.key) {
                        @if (slider.section) {
                            <div class="controls__section-label">
                                {{ slider.section }}
                            </div>
                        }
                        <div class="controls__row">
                            <div class="controls__row-top">
                                <span class="controls__label">{{
                                    slider.label
                                }}</span>
                                <span class="controls__val">{{
                                    svc.inputs()[slider.key]
                                        | rvbFormat: slider.format
                                }}</span>
                            </div>
                            <input
                                type="range"
                                [min]="slider.min"
                                [max]="slider.max"
                                [step]="slider.step"
                                [value]="svc.inputs()[slider.key]"
                                (input)="onSlider(slider, $event)"
                            />
                        </div>
                    }
                </div>
            }

            <!-- RENT TAB -->
            @if (svc.activeTab() === "rent") {
                <div class="controls__panel" role="tabpanel">
                    <div class="controls__section-label">Starting rent</div>
                    <p class="controls__note">
                        Starting rent is set at
                        <strong>0.8% of the home price</strong> — a common rule
                        of thumb that ties the comparison to a true
                        apples-to-apples scenario.
                    </p>
                    <div class="controls__row">
                        <div class="controls__row-top">
                            <span class="controls__label"
                                >Starting monthly rent</span
                            >
                            <span class="controls__val">{{
                                svc.initRent() | rvbFormat: "dollar"
                            }}</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="0"
                            step="1"
                            [value]="svc.initRent()"
                            disabled
                            class="controls__range--locked"
                        />
                    </div>
                    @for (slider of rentSliders; track slider.key) {
                        @if (slider.section) {
                            <div class="controls__section-label">
                                {{ slider.section }}
                            </div>
                        }
                        <div class="controls__row">
                            <div class="controls__row-top">
                                <span class="controls__label">{{
                                    slider.label
                                }}</span>
                                <span class="controls__val">{{
                                    svc.inputs()[slider.key]
                                        | rvbFormat: slider.format
                                }}</span>
                            </div>
                            <input
                                type="range"
                                [min]="slider.min"
                                [max]="slider.max"
                                [step]="slider.step"
                                [value]="svc.inputs()[slider.key]"
                                (input)="onSlider(slider, $event)"
                            />
                        </div>
                    }
                </div>
            }

            <!-- MARKET TAB -->
            @if (svc.activeTab() === "market") {
                <div class="controls__panel" role="tabpanel">
                    @for (slider of marketSliders; track slider.key) {
                        @if (slider.section) {
                            <div class="controls__section-label">
                                {{ slider.section }}
                            </div>
                        }
                        @if (slider.key === "marketReturn") {
                            <p class="controls__note">
                                The down payment (+ closing costs) is modeled as
                                invested in the market. Any monthly savings from
                                renting are also invested.
                            </p>
                        }
                        <div class="controls__row">
                            <div class="controls__row-top">
                                <span class="controls__label">{{
                                    slider.label
                                }}</span>
                                <span class="controls__val">{{
                                    svc.inputs()[slider.key]
                                        | rvbFormat: slider.format
                                }}</span>
                            </div>
                            <input
                                type="range"
                                [min]="slider.min"
                                [max]="slider.max"
                                [step]="slider.step"
                                [value]="svc.inputs()[slider.key]"
                                (input)="onSlider(slider, $event)"
                            />
                        </div>
                    }
                </div>
            }

            <!-- TIME TAB -->
            @if (svc.activeTab() === "time") {
                <div class="controls__panel" role="tabpanel">
                    @for (slider of timeSliders; track slider.key) {
                        @if (slider.section) {
                            <div class="controls__section-label">
                                {{ slider.section }}
                            </div>
                        }
                        <div class="controls__row">
                            <div class="controls__row-top">
                                <span class="controls__label">{{
                                    slider.label
                                }}</span>
                                <span class="controls__val">{{
                                    svc.inputs()[slider.key]
                                        | rvbFormat: slider.format
                                }}</span>
                            </div>
                            <input
                                type="range"
                                [min]="slider.min"
                                [max]="slider.max"
                                [step]="slider.step"
                                [value]="svc.inputs()[slider.key]"
                                (input)="onSlider(slider, $event)"
                            />
                        </div>
                    }
                    <p class="controls__note" style="margin-top: 8px;">
                        The chart will extend to your full horizon. Buying
                        generally breaks even versus renting between years 5–8,
                        depending on market conditions.
                    </p>
                    <div
                        class="controls__section-label"
                        style="margin-top: 20px;"
                    >
                        Breakeven estimate
                    </div>
                    <div class="controls__breakeven">
                        <div class="controls__breakeven-val">
                            {{
                                svc.summary().breakevenYear
                                    ? "Year " + svc.summary().breakevenYear
                                    : "> " + svc.inputs().horizon + " yr"
                            }}
                        </div>
                        <div class="controls__breakeven-lbl">
                            Estimated breakeven year
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styleUrl: "./rvb-controls-panel.component.scss",
})
export class RvbControlsPanelComponent {
    protected readonly svc = inject(RentVsBuyService);

    protected readonly tabs: TabDef[] = [
        { id: "buy", icon: "/icons/gray-house.svg", label: "Buy" },
        { id: "rent", icon: "/icons/key.svg", label: "Rent" },
        { id: "market", icon: "/icons/graph-blue.svg", label: "Market" },
        { id: "time", icon: "/icons/clock-analog.svg", label: "Time" },
    ];

    protected readonly buySliders = SLIDER_DEFS.filter((s) => s.tab === "buy");
    protected readonly rentSliders = SLIDER_DEFS.filter(
        (s) => s.tab === "rent"
    );
    protected readonly marketSliders = SLIDER_DEFS.filter(
        (s) => s.tab === "market"
    );
    protected readonly timeSliders = SLIDER_DEFS.filter(
        (s) => s.tab === "time"
    );

    protected onSlider(slider: RvbSliderDef, event: Event): void {
        const val = parseFloat((event.target as HTMLInputElement).value);
        this.svc.updateInput(slider.key, val);
    }
}
